import React from 'react';
import { Check } from 'lucide-react';
import { formatPriceCents } from '@/utils/formatPrice';
import { TIERS, TIER_PRICING_CENTS, getTierDisplayName } from '@/utils/tierUtils';
import type { TemplateTier } from '@/types/template';

// Marketing copy per tier; names and prices come from the canonical tier
// definitions in `tierUtils` so this page can't drift from what Stripe bills.
const tierDetails: Record<TemplateTier, { description: string; features: string[] }> = {
  standard: {
    description: 'Sites built from standard templates.',
    features: [
      'Hosted subdomain',
      'Custom domain support',
    ],
  },
  premium: {
    description: 'Sites built from premium templates.',
    features: [
      'Everything in Standard',
      'Premium template designs',
    ],
  },
};

const tiers = TIERS.map((tier) => ({
  id: tier,
  name: getTierDisplayName(tier),
  priceCents: TIER_PRICING_CENTS[tier],
  ...tierDetails[tier],
}));

const universalGuarantees = [
  'SSL certificates on every site',
  'Automatic deploys from your template',
  '99.9% uptime',
  'Upgrade or downgrade any site at any time',
];

const Pricing: React.FC = () => {
  return (
    <div className="min-h-screen">
      <div className="max-w-7xl mx-auto pt-5 pb-8">
        <div className="mb-8">
          <h1 className="text-3xl sm:text-4xl font-bold text-foreground">Pricing</h1>
          <p className="text-sm text-muted-foreground mt-2">
            Each site is billed individually. The price depends on the tier of the
            template it&rsquo;s built from.
          </p>
        </div>

        <div className="space-y-12">
          {/* Plans */}
          <section className="flex gap-8">
            <div className="w-48 flex-shrink-0">
              <h2 className="text-xl font-medium text-foreground">Plans</h2>
            </div>
            <div className="flex-1 min-w-0">
              <dl className="space-y-8">
                {tiers.map((tier) => (
                  <div key={tier.id} className="flex items-baseline gap-8">
                    <dt className="w-32 flex-shrink-0">
                      <p className="text-base font-medium text-foreground">{tier.name}</p>
                      <p className="text-sm text-muted-foreground">
                        {`${formatPriceCents(tier.priceCents)} per site`}
                      </p>
                    </dt>
                    <dd className="flex-1 min-w-0">
                      <p className="text-sm text-foreground mb-3">{tier.description}</p>
                      <ul className="space-y-2">
                        {tier.features.map((feature) => (
                          <li key={feature} className="flex items-center gap-2 text-sm text-foreground">
                            <Check className="h-4 w-4 stroke-[1.5] text-foreground flex-shrink-0" />
                            {feature}
                          </li>
                        ))}
                      </ul>
                    </dd>
                  </div>
                ))}
              </dl>
            </div>
          </section>

          {/* What's included */}
          <section className="flex gap-8">
            <div className="w-48 flex-shrink-0">
              <h2 className="text-xl font-medium text-foreground">What's included</h2>
            </div>
            <div className="flex-1 min-w-0">
              <ul className="space-y-2">
                {universalGuarantees.map((guarantee) => (
                  <li key={guarantee} className="flex items-center gap-2 text-sm text-foreground">
                    <Check className="h-4 w-4 stroke-[1.5] text-foreground flex-shrink-0" />
                    {guarantee}
                  </li>
                ))}
              </ul>
            </div>
          </section>

          {/* Upgrade flow */}
          <section className="flex gap-8">
            <div className="w-48 flex-shrink-0">
              <h2 className="text-xl font-medium text-foreground">How to upgrade</h2>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm text-foreground">
                Open any site from the Sites page, then use the Upgrade action in its
                subscription section. Each site is billed on its own schedule.
              </p>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default Pricing;
